/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ClipboardCheck, Plus, Copy, Archive, Eye, RefreshCw, Filter,
  AlertTriangle, ArrowRight, X, CheckCircle2, ChevronRight,
} from 'lucide-react';
import TrackingTemplateGenerator from '@/components/admin/TrackingTemplateGenerator';

const FORM_TYPES = ['daily', 'event', 'one_time'];
const QUESTION_TYPES = [
  'slider', 'single_choice', 'multi_choice', 'number',
  'text', 'textarea', 'date', 'time', 'yes_no', 'scale_1_10',
];
const FREQUENCIES = ['daily', 'twice_daily', 'once', 'after_event', 'custom'];
const SEVERITY_TONE = {
  low: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  high: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  urgent: 'bg-red-500/20 text-red-300 border-red-500/40',
};

export default function TrackingOS() {
  const { user } = useAuth();
  const [tab, setTab] = useState('templates');

  if (!user) return null;
  const allowed = ['admin', 'counselor', 'health_coach', 'doctor'].includes(user.role);
  if (!allowed) {
    return (
      <AdminLayout>
        <div className="p-8 text-white/70" data-testid="tracking-os-forbidden">
          You do not have access to Tracking OS.
        </div>
      </AdminLayout>
    );
  }

  const isAdmin = user.role === 'admin';

  return (
    <AdminLayout>
      <div className="px-8 py-8 max-w-[1400px] mx-auto" data-testid="tracking-os-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <ClipboardCheck className="w-6 h-6 text-cyan-300" />
              <h1 className="text-2xl font-medium text-white" data-testid="tracking-os-title">
                Tracking OS
              </h1>
              <Badge className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-xs">V1</Badge>
            </div>
            <p className="text-sm text-white/50 max-w-2xl">
              Schema-driven daily, event-based, and one-time observation forms.
              Author templates, assign to subscribers, review responses, and act on flags.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-adm-surface/5 border border-white/10">
            <TabsTrigger value="templates"   data-testid="tab-templates">Templates</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
            <TabsTrigger value="responses"   data-testid="tab-responses">Responses</TabsTrigger>
            <TabsTrigger value="insights"    data-testid="tab-insights">Insights</TabsTrigger>
            <TabsTrigger value="flags"       data-testid="tab-flags">Flags</TabsTrigger>
          </TabsList>

          <TabsContent value="templates"   className="mt-6"><TemplatesTab   isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="assignments" className="mt-6"><AssignmentsTab isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="responses"   className="mt-6"><ResponsesTab /></TabsContent>
          <TabsContent value="insights"    className="mt-6"><InsightsTab /></TabsContent>
          <TabsContent value="flags"       className="mt-6"><FlagsTab /></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

// ════════════════════ Templates ════════════════════
const FREQUENCY_BY_FORM_TYPE = {
  one_time: 'once',
  daily: 'daily',
  event: 'after_event',
};

function buildAssignmentPrefill(saved) {
  if (!saved?.id) return null;
  return {
    template: saved,
    template_id: saved.id,
    assignment_title: saved.subscriber_title || saved.name || '',
    frequency: FREQUENCY_BY_FORM_TYPE[saved.form_type] || 'daily',
    linked_event_reason: saved.linked_event_reason || saved.default_assignment?.linked_event_reason || '',
    linked_condition: (saved.condition_tags && saved.condition_tags[0]) || '',
    subscriber_id: '', // never auto-pick a subscriber
  };
}

function TemplatesTab({ isAdmin }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | template doc
  const [preview, setPreview] = useState(null);
  const [assignPrefill, setAssignPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracking/templates`);
      setTemplates(r.data?.templates || []);
    } catch {
      toast.error('Could not load templates');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const archive = async (t) => {
    if (!window.confirm(`Archive "${t.name}"? Active assignments will be paused.`)) return;
    try {
      await axios.post(`${API}/tracking/templates/${t.id}/archive`);
      toast.success('Template archived');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };
  const clone = async (t) => {
    try {
      const r = await axios.post(`${API}/tracking/templates/${t.id}/clone`);
      toast.success('Template cloned');
      load();
      setEditor(r.data);
    } catch {
      toast.error('Clone failed');
    }
  };

  return (
    <div data-testid="templates-tab">
      {isAdmin && (
        <TrackingTemplateGenerator
          onSaved={(saved, opts) => {
            load();
            if (opts?.assignAfter) setAssignPrefill(buildAssignmentPrefill(saved));
          }}
        />
      )}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-sm text-white/60">
          {loading ? 'Loading templates…' : `${templates.length} templates`}
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="reload-templates-btn"
            variant="outline"
            size="sm"
            onClick={load}
            className="border-white/15 text-white/70 hover:bg-white/5"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isAdmin && (
            <Button
              data-testid="new-template-btn"
              onClick={() => setEditor(blankTemplate())}
              className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Template
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3" data-testid="templates-list">
        {templates.map((t) => (
          <TemplateRow
            key={t.id}
            t={t}
            isAdmin={isAdmin}
            onEdit={() => setEditor(t)}
            onClone={() => clone(t)}
            onArchive={() => archive(t)}
            onPreview={() => setPreview(t)}
          />
        ))}
        {!loading && templates.length === 0 && (
          <div className="text-sm text-white/40 italic">No templates yet.</div>
        )}
      </div>

      {editor && (
        <TemplateEditor
          template={editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
          canEdit={isAdmin}
        />
      )}
      {preview && (
        <TemplatePreviewModal template={preview} onClose={() => setPreview(null)} />
      )}
      {assignPrefill && (
        <NewAssignmentDialog
          prefill={assignPrefill}
          onClose={() => setAssignPrefill(null)}
          onCreated={() => { setAssignPrefill(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateRow({ t, isAdmin, onEdit, onClone, onArchive, onPreview }) {
  return (
    <div
      data-testid={`template-row-${t.id}`}
      className="rounded-lg border border-white/10 bg-adm-surface/5 p-4 flex items-start justify-between gap-4 flex-wrap"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-white font-medium" data-testid={`template-name-${t.id}`}>{t.name}</span>
          <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 text-xs uppercase tracking-wide">
            {t.form_type || 'daily'}
          </Badge>
          <Badge
            className={`text-xs ${
              t.status === 'active'   ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
              t.status === 'archived' ? 'bg-slate-500/15 text-slate-400 border-slate-500/30' :
                                         'bg-amber-500/15 text-amber-300 border-amber-500/30'
            }`}
          >
            {t.status}
          </Badge>
          {t.is_default && (
            <Badge className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-xs">
              Default
            </Badge>
          )}
        </div>
        <p className="text-xs text-white/50 line-clamp-2 max-w-2xl">{t.description}</p>
        <div className="text-[11px] text-white/40 mt-1">
          {(t.questions || []).length} questions
          {' · '}{(t.red_flag_rules || []).length} flag rules
          {(t.condition_tags || []).length > 0 && (
            <> · tags: {(t.condition_tags || []).join(', ')}</>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline"
          className="border-white/15 text-white/70 hover:bg-white/5"
          data-testid={`preview-template-${t.id}`} onClick={onPreview}>
          <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
        </Button>
        {isAdmin && (
          <>
            <Button size="sm" variant="outline"
              className="border-white/15 text-white/70 hover:bg-white/5"
              data-testid={`edit-template-${t.id}`} onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" variant="outline"
              className="border-white/15 text-white/70 hover:bg-white/5"
              data-testid={`clone-template-${t.id}`} onClick={onClone}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Clone
            </Button>
            {!t.is_default && t.status !== 'archived' && (
              <Button size="sm" variant="outline"
                className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                data-testid={`archive-template-${t.id}`} onClick={onArchive}>
                <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function blankTemplate() {
  return {
    id: null, name: '', description: '', category: 'general',
    condition_tags: [], form_type: 'daily',
    questions: [{ key: 'q1', label: '', type: 'scale_1_10', min: 1, max: 10, required: false }],
    scoring_rules: {}, red_flag_rules: [],
    twinflo_mapping: {}, subscriber_feedback_rules: { default: '' },
    admin_interpretation_prompt: '', status: 'draft',
  };
}

function TemplateEditor({ template, onClose, onSaved, canEdit }) {
  const [t, setT] = useState({ ...template, questions: [...(template.questions || [])] });
  const [saving, setSaving] = useState(false);

  const isNew = !t.id;
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  const setQ = (i, k, v) => setT((p) => {
    const qs = [...p.questions];
    qs[i] = { ...qs[i], [k]: v };
    return { ...p, questions: qs };
  });
  const addQ = () => setT((p) => ({ ...p, questions: [...p.questions, { key: `q${p.questions.length + 1}`, label: '', type: 'scale_1_10', required: false }] }));
  const rmQ = (i) => setT((p) => ({ ...p, questions: p.questions.filter((_, idx) => idx !== i) }));

  const addRule = () => setT((p) => ({
    ...p,
    red_flag_rules: [...(p.red_flag_rules || []), {
      id: `rf_${Date.now().toString(36)}`,
      severity: 'medium', reason: '', suggested_action: '', assigned_to_role: 'counselor',
      all_of: [{ key: '', op: '<=', value: 3 }],
    }],
  }));
  const setRule = (i, k, v) => setT((p) => {
    const rs = [...(p.red_flag_rules || [])];
    rs[i] = { ...rs[i], [k]: v };
    return { ...p, red_flag_rules: rs };
  });
  const setCond = (ri, ci, k, v) => setT((p) => {
    const rs = [...(p.red_flag_rules || [])];
    const cs = [...(rs[ri].all_of || [])];
    cs[ci] = { ...cs[ci], [k]: k === 'value' ? Number(v) : v };
    rs[ri] = { ...rs[ri], all_of: cs };
    return { ...p, red_flag_rules: rs };
  });
  const addCond = (ri) => setT((p) => {
    const rs = [...(p.red_flag_rules || [])];
    rs[ri] = { ...rs[ri], all_of: [...(rs[ri].all_of || []), { key: '', op: '<=', value: 3 }] };
    return { ...p, red_flag_rules: rs };
  });
  const rmRule = (i) => setT((p) => ({ ...p, red_flag_rules: p.red_flag_rules.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!t.name?.trim()) { toast.error('Name is required'); return; }
    if (!t.questions?.length) { toast.error('Add at least one question'); return; }
    for (const q of t.questions) {
      if (!q.key?.trim()) { toast.error('Each question needs a `key`'); return; }
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/tracking/templates`, t);
        toast.success('Template created');
      } else {
        await axios.patch(`${API}/tracking/templates/${t.id}`, t);
        toast.success('Template updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl bg-[#1a1f2e] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="text-white">{isNew ? 'New Template' : 'Edit Template'}</DialogTitle>
          <DialogDescription className="text-white/50">
            Schema-driven form definition. Used to render subscriber observation pages.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Field label="Name">
            <Input data-testid="template-name-input" value={t.name} onChange={(e) => set('name', e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Description">
            <Textarea data-testid="template-description-input" rows={2} value={t.description} onChange={(e) => set('description', e.target.value)} disabled={!canEdit} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Form type">
              <Select value={t.form_type} onValueChange={(v) => set('form_type', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="template-form-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORM_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={t.status} onValueChange={(v) => set('status', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="template-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="archived">archived</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Condition tags (comma-separated)">
            <Input
              data-testid="template-tags-input"
              value={(t.condition_tags || []).join(', ')}
              onChange={(e) => set('condition_tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              disabled={!canEdit}
            />
          </Field>

          {/* Questions */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/70">Questions</span>
              {canEdit && (
                <Button size="sm" variant="outline" data-testid="add-question-btn" onClick={addQ}
                  className="border-white/15 text-white/70 hover:bg-white/5">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add question
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {t.questions.map((q, i) => (
                <div key={i} className="rounded border border-white/10 bg-black/20 p-3 space-y-2" data-testid={`question-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Input className="col-span-3" placeholder="key (snake_case)"
                      value={q.key || ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'key', e.target.value)} />
                    <Input className="col-span-5" placeholder="label"
                      value={q.label || ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'label', e.target.value)} />
                    <Select value={q.type || 'scale_1_10'} onValueChange={(v) => setQ(i, 'type', v)} disabled={!canEdit}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {QUESTION_TYPES.map((qt) => <SelectItem key={qt} value={qt}>{qt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {canEdit && (
                      <Button variant="ghost" size="sm" className="col-span-1 text-rose-300" onClick={() => rmQ(i)} data-testid={`remove-question-${i}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <Input className="col-span-2" type="number" placeholder="min"
                      value={q.min ?? ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'min', e.target.value === '' ? null : Number(e.target.value))} />
                    <Input className="col-span-2" type="number" placeholder="max"
                      value={q.max ?? ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'max', e.target.value === '' ? null : Number(e.target.value))} />
                    <Input className="col-span-2" placeholder="unit"
                      value={q.unit || ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'unit', e.target.value)} />
                    <Input className="col-span-3" placeholder="help_text"
                      value={q.help_text || ''} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'help_text', e.target.value)} />
                    <Input className="col-span-3" placeholder="options (comma) — for choice types"
                      value={(q.options || []).join(', ')} disabled={!canEdit}
                      onChange={(e) => setQ(i, 'options', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Red flag rules */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/70">Red-flag rules (deterministic)</span>
              {canEdit && (
                <Button size="sm" variant="outline" data-testid="add-rule-btn" onClick={addRule}
                  className="border-white/15 text-white/70 hover:bg-white/5">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add rule
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {(t.red_flag_rules || []).map((r, ri) => (
                <div key={ri} className="rounded border border-white/10 bg-black/20 p-3 space-y-2" data-testid={`rule-row-${ri}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Select value={r.severity || 'medium'} onValueChange={(v) => setRule(ri, 'severity', v)} disabled={!canEdit}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(SEVERITY_TONE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-5" placeholder="reason (shown to admin)"
                      value={r.reason || ''} disabled={!canEdit}
                      onChange={(e) => setRule(ri, 'reason', e.target.value)} />
                    <Input className="col-span-4" placeholder="suggested action"
                      value={r.suggested_action || ''} disabled={!canEdit}
                      onChange={(e) => setRule(ri, 'suggested_action', e.target.value)} />
                    {canEdit && (
                      <Button variant="ghost" size="sm" className="col-span-1 text-rose-300" onClick={() => rmRule(ri)} data-testid={`remove-rule-${ri}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {(r.all_of || []).map((c, ci) => (
                      <div key={ci} className="grid grid-cols-12 gap-2">
                        <Input className="col-span-5" placeholder="answer key (e.g. sleep_quality)"
                          value={c.key || ''} disabled={!canEdit}
                          onChange={(e) => setCond(ri, ci, 'key', e.target.value)} />
                        <Select value={c.op || '<='} onValueChange={(v) => setCond(ri, ci, 'op', v)} disabled={!canEdit}>
                          <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['>', '>=', '<', '<=', '==', '!='].map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className="col-span-4" type="number" placeholder="value"
                          value={c.value ?? ''} disabled={!canEdit}
                          onChange={(e) => setCond(ri, ci, 'value', e.target.value)} />
                      </div>
                    ))}
                    {canEdit && (
                      <Button size="sm" variant="ghost" className="text-cyan-300 px-2 h-7" onClick={() => addCond(ri)}>
                        + AND condition
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reflection */}
          <div className="border-t border-white/10 pt-4">
            <Field label="Subscriber reflection (default)">
              <Textarea data-testid="reflection-default" rows={2}
                value={(t.subscriber_feedback_rules || {}).default || ''}
                onChange={(e) => set('subscriber_feedback_rules', { ...(t.subscriber_feedback_rules || {}), default: e.target.value })}
                disabled={!canEdit} />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/60">Close</Button>
          {canEdit && (
            <Button data-testid="save-template-btn" onClick={save} disabled={saving}
              className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30">
              {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function TemplatePreviewModal({ template, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-[#1a1f2e] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="template-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Eye className="w-4 h-4" /> Subscriber Preview · {template.name}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            This is roughly what the subscriber sees. Read-only.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          {(template.questions || []).map((q, i) => (
            <div key={i} className="rounded border border-white/10 bg-black/20 p-3">
              <div className="text-sm text-white">{q.label || q.key}{q.required && <span className="text-rose-400 ml-1">*</span>}</div>
              {q.help_text && <div className="text-xs text-white/50 mt-0.5">{q.help_text}</div>}
              <div className="mt-2 text-xs text-white/40">
                <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{q.type}</span>
                {q.min != null && <> · min {q.min}</>}
                {q.max != null && <> · max {q.max}</>}
                {q.unit && <> · {q.unit}</>}
              </div>
            </div>
          ))}
          <div className="rounded border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-200/80">
            On submit: <em>{(template.subscriber_feedback_rules || {}).default || 'Your observation has been saved.'}</em>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/60">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════ Assignments ════════════════════
function AssignmentsTab({ isAdmin }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracking/assignments`);
      setList(r.data?.assignments || []);
    } catch {
      toast.error('Could not load assignments');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (a, status) => {
    try {
      await axios.patch(`${API}/tracking/assignments/${a.id}`, { status });
      toast.success(`Assignment ${status}`);
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div data-testid="assignments-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-sm text-white/60">
          {loading ? 'Loading…' : `${list.length} assignments`}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} className="border-white/15 text-white/70 hover:bg-white/5" data-testid="reload-assignments-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isAdmin && (
            <Button data-testid="new-assignment-btn" onClick={() => setShowForm(true)}
              className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30">
              <Plus className="w-4 h-4 mr-1.5" /> New Assignment
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-2" data-testid="assignments-list">
        {list.map((a) => (
          <div key={a.id} className="rounded-lg border border-white/10 bg-adm-surface/5 p-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`assignment-row-${a.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white text-sm">{a.assignment_title}</span>
                <Badge className="text-[10px] uppercase tracking-wider bg-slate-700/50 text-slate-300 border-slate-600">
                  {a.template_form_type || '—'}
                </Badge>
                <Badge className={`text-xs ${
                  a.status === 'active'    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                  a.status === 'paused'    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                  a.status === 'expired'   ? 'bg-rose-500/15 text-rose-300 border-rose-500/30' :
                                              'bg-slate-500/15 text-slate-300 border-slate-500/30'
                }`}>{a.status}</Badge>
                {a.required && <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-xs">required</Badge>}
              </div>
              <div className="text-xs text-white/50 mt-0.5">
                Subscriber: <span className="text-white/80">{a.subscriber_id}</span>
                {' · '}Template: <span className="text-white/80">{a.template_name}</span>
                {' · '}Frequency: <span className="text-white/80">{a.frequency}</span>
                {a.start_date && <> · {a.start_date}</>}{a.end_date && <> → {a.end_date}</>}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-1.5">
                {a.status === 'active' && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, 'paused')}
                    className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                    data-testid={`pause-assignment-${a.id}`}>Pause</Button>
                )}
                {a.status === 'paused' && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, 'active')}
                    className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`resume-assignment-${a.id}`}>Resume</Button>
                )}
                {(a.status === 'active' || a.status === 'paused') && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, 'expired')}
                    className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                    data-testid={`expire-assignment-${a.id}`}>End</Button>
                )}
              </div>
            )}
          </div>
        ))}
        {!loading && list.length === 0 && (
          <div className="text-sm text-white/40 italic">No assignments yet.</div>
        )}
      </div>

      {showForm && (
        <NewAssignmentDialog
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function NewAssignmentDialog({ onClose, onCreated, prefill }) {
  const [templates, setTemplates] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [form, setForm] = useState({
    subscriber_id: prefill?.subscriber_id || '',
    template_id: prefill?.template_id || '',
    assignment_title: prefill?.assignment_title || '',
    start_date: '',
    end_date: '',
    frequency: prefill?.frequency || 'daily',
    required: false,
    linked_condition: prefill?.linked_condition || '',
    linked_event_reason: prefill?.linked_event_reason || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/tracking/templates?status=active`),
      axios.get(`${API}/users`),
    ]).then(([t, u]) => {
      let list = t.data?.templates || [];
      // If a prefill template is supplied (typically a freshly saved draft that
      // wouldn't show up under status=active), prepend it so admins can see and
      // confirm the preselection before creating the assignment.
      if (prefill?.template && !list.find((x) => x.id === prefill.template.id)) {
        list = [prefill.template, ...list];
      }
      setTemplates(list);
      setSubscribers((u.data || []).filter((x) => x.role === 'subscriber'));
    }).catch(() => toast.error('Could not load form data'));
  }, [prefill]);

  const submit = async () => {
    if (!form.subscriber_id || !form.template_id) { toast.error('Pick subscriber and template'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tracking/assignments`, form);
      toast.success('Assignment created');

      // V2.2 — Activate the just-saved draft only AFTER assignment succeeds.
      // We never auto-activate before this point. If activation fails, the
      // assignment still stands and we surface a warning so the admin can
      // activate manually from the templates tab.
      const prefilledDraft = prefill?.template;
      if (prefilledDraft?.id
          && prefilledDraft.status === 'draft'
          && form.template_id === prefilledDraft.id) {
        try {
          await axios.patch(`${API}/tracking/templates/${prefilledDraft.id}`, { status: 'active' });
          toast.success('Template activated after assignment.');
        } catch {
          toast.warning('Assignment created, but template activation failed — activate manually from Templates.');
        }
      }

      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl bg-[#1a1f2e] border-white/10" data-testid="new-assignment-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">New Assignment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Subscriber">
            <Select value={form.subscriber_id} onValueChange={(v) => setForm({ ...form, subscriber_id: v })}>
              <SelectTrigger data-testid="assignment-subscriber-select"><SelectValue placeholder="Select subscriber" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {subscribers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Template">
            <Select value={form.template_id} onValueChange={(v) => {
              const t = templates.find((x) => x.id === v);
              setForm({ ...form, template_id: v, assignment_title: form.assignment_title || (t?.name || '') });
            }}>
              <SelectTrigger data-testid="assignment-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.form_type})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assignment title">
            <Input data-testid="assignment-title-input" value={form.assignment_title} onChange={(e) => setForm({ ...form, assignment_title: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency">
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="assignment-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Required?">
              <div className="flex items-center gap-2 h-10">
                <input type="checkbox" data-testid="assignment-required-checkbox"
                  checked={form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                <span className="text-sm text-white/70">Mark as required</span>
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" data-testid="assignment-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="End date"><Input type="date" data-testid="assignment-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Linked condition"><Input value={form.linked_condition} onChange={(e) => setForm({ ...form, linked_condition: e.target.value })} placeholder="e.g. parkinson" /></Field>
            <Field label="Linked event reason"><Input value={form.linked_event_reason} onChange={(e) => setForm({ ...form, linked_event_reason: e.target.value })} placeholder="e.g. post_dbs" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/60">Cancel</Button>
          <Button data-testid="create-assignment-btn" onClick={submit} disabled={saving}
            className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════ Responses ════════════════════
function ResponsesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ subscriber_id: '', template_id: '', review_status: '' });
  const [detail, setDetail] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await axios.get(`${API}/tracking/responses`, { params });
      setList(r.data?.responses || []);
    } catch {
      toast.error('Could not load responses');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/tracking/templates`).then((r) => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);
  const tplName = useMemo(() => Object.fromEntries((templates || []).map((t) => [t.id, t.name])), [templates]);

  return (
    <div data-testid="responses-tab">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div className="grid grid-cols-3 gap-2 flex-1 min-w-[400px]">
          <Input placeholder="Subscriber id…" value={filters.subscriber_id} onChange={(e) => setFilters({ ...filters, subscriber_id: e.target.value })} data-testid="responses-filter-subscriber" />
          <Select value={filters.template_id || '__all__'} onValueChange={(v) => setFilters({ ...filters, template_id: v === '__all__' ? '' : v })}>
            <SelectTrigger data-testid="responses-filter-template"><SelectValue placeholder="Template" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All templates</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.review_status || '__all__'} onValueChange={(v) => setFilters({ ...filters, review_status: v === '__all__' ? '' : v })}>
            <SelectTrigger data-testid="responses-filter-review"><SelectValue placeholder="Review status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="new">new</SelectItem>
              <SelectItem value="reviewed">reviewed</SelectItem>
              <SelectItem value="action_created">action_created</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="border-white/15 text-white/70 hover:bg-white/5" data-testid="reload-responses-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid gap-2" data-testid="responses-list">
        {list.map((r) => (
          <div key={r.id} className="rounded border border-white/10 bg-adm-surface/5 p-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`response-row-${r.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white text-sm">{r.observation_date}</span>
                <Badge className="text-[10px] uppercase bg-slate-700/50 text-slate-300 border-slate-600">{r.form_type}</Badge>
                <Badge className={`text-xs ${
                  r.admin_review_status === 'reviewed'        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                  r.admin_review_status === 'action_created'  ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' :
                                                                 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                }`}>{r.admin_review_status}</Badge>
                {(r.flags || []).length > 0 && (
                  <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" /> {r.flags.length} flag{r.flags.length > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-white/50 mt-0.5">
                Subscriber: {r.subscriber_id} · Template: {tplName[r.template_id] || r.template_id}
                {r.computed_scores?.wellbeing_index != null && <> · Wellbeing index: <span className="text-white/80">{r.computed_scores.wellbeing_index}</span></>}
                {r.source && <> · {r.source}</>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setDetail(r)}
              className="border-white/15 text-white/70 hover:bg-white/5"
              data-testid={`open-response-${r.id}`}>
              Open <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        ))}
        {!loading && list.length === 0 && (
          <div className="text-sm text-white/40 italic">No responses match these filters yet.</div>
        )}
      </div>

      {detail && <ResponseDetail response={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function ResponseDetail({ response, onClose, onChanged }) {
  const [note, setNote] = useState(response.admin_note || '');
  const [saving, setSaving] = useState(false);
  const [hints, setHints] = useState(null);

  useEffect(() => {
    if (!response?.subscriber_id) return;
    let cancelled = false;
    axios.get(`${API}/tracking/pattern-hints/${response.subscriber_id}?days=30&window=14`)
      .then((r) => { if (!cancelled) setHints(r.data?.hints || []); })
      .catch(() => { if (!cancelled) setHints([]); });
    return () => { cancelled = true; };
  }, [response?.subscriber_id]);

  const review = async (status) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/tracking/responses/${response.id}/review`,
        { admin_review_status: status, admin_note: note });
      toast.success('Saved');
      onChanged();
      onClose();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-[#1a1f2e] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="response-detail">
        <DialogHeader>
          <DialogTitle className="text-white">Response · {response.observation_date}</DialogTitle>
          <DialogDescription className="text-white/50">Subscriber: {response.subscriber_id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {response.computed_scores && Object.keys(response.computed_scores).length > 0 && (
            <div className="rounded border border-white/10 bg-black/20 p-3">
              <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Computed scores</div>
              <pre className="text-sm text-white/80 whitespace-pre-wrap" data-testid="response-scores">{JSON.stringify(response.computed_scores, null, 2)}</pre>
            </div>
          )}
          <div className="rounded border border-white/10 bg-black/20 p-3">
            <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Answers</div>
            <pre className="text-sm text-white/80 whitespace-pre-wrap" data-testid="response-answers">{JSON.stringify(response.answers, null, 2)}</pre>
          </div>
          {(response.flags || []).length > 0 && (
            <div className="rounded border border-rose-500/20 bg-rose-500/5 p-3" data-testid="response-flags">
              <div className="text-xs uppercase tracking-wider text-rose-300 mb-1">Flags</div>
              <ul className="space-y-1">
                {response.flags.map((f, i) => (
                  <li key={i} className="text-sm">
                    <Badge className={`mr-2 text-[11px] ${SEVERITY_TONE[f.severity] || ''}`}>{f.severity}</Badge>
                    <span className="text-white/80">{f.reason}</span>
                    {f.suggested_action && <span className="text-white/50"> — {f.suggested_action}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hints && hints.length > 0 && (
            <div className="rounded border border-cyan-500/20 bg-cyan-500/5 p-3" data-testid="response-pattern-hints">
              <div className="text-xs uppercase tracking-wider text-cyan-300 mb-2">Pattern hints (last 14 days)</div>
              <ul className="space-y-1.5">
                {hints.map((h) => (
                  <li key={h.id} data-testid={`response-hint-${h.id}`}
                    className={`flex items-start gap-2 text-sm rounded border px-3 py-2 ${
                      h.severity === 'concern' ? 'border-rose-500/25 bg-rose-500/5 text-rose-200' :
                      h.severity === 'watch'   ? 'border-amber-500/25 bg-amber-500/5 text-amber-200' :
                                                  'border-cyan-500/20 bg-cyan-500/5 text-cyan-200'
                    }`}>
                    <span className="text-[10px] uppercase tracking-wider opacity-70 shrink-0 mt-0.5">{h.severity}</span>
                    <span className="leading-relaxed">{h.text}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-white/40 mt-2 italic">
                Deterministic patterns from the last 14 days of submissions. No AI.
              </p>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Admin note</label>
            <Textarea data-testid="response-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/60">Close</Button>
          <Button onClick={() => review('reviewed')} disabled={saving}
            className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30"
            data-testid="mark-reviewed-btn">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark reviewed
          </Button>
          <Button onClick={() => review('action_created')} disabled={saving}
            className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30"
            data-testid="mark-action-btn">
            <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Action created
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════ Insights ════════════════════
function InsightsTab() {
  const [subId, setSubId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/users`).then((r) => setSubs((r.data || []).filter((x) => x.role === 'subscriber'))).catch(() => {});
  }, []);

  const load = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracking/insights/${sid}?days=30`);
      setData(r.data);
    } catch {
      toast.error('Could not load insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="insights-tab" className="space-y-4">
      <div className="flex gap-2 items-end">
        <div className="flex-1 max-w-md">
          <Field label="Subscriber">
            <Select value={subId} onValueChange={(v) => { setSubId(v); load(v); }}>
              <SelectTrigger data-testid="insights-subscriber-select"><SelectValue placeholder="Select a subscriber" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(subId)} disabled={!subId || loading}
          className="border-white/15 text-white/70 hover:bg-white/5" data-testid="reload-insights-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {!data && !loading && (
        <div className="text-sm text-white/40 italic">Pick a subscriber to see their tracking insights.</div>
      )}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Avg 7d wellbeing" value={data.average_7d ?? '—'} testid="stat-avg-7d" />
          <Stat label={`Avg ${Math.min(30, data.window_days)}d`} value={data.average_window ?? '—'} testid="stat-avg-window" />
          <Stat label="Direction" value={data.direction} testid="stat-direction" tone={
            data.direction === 'improving' ? 'emerald' :
            data.direction === 'worsening' ? 'rose' : 'slate'
          } />
          <Stat label="Open flags" value={data.open_flags ?? 0} testid="stat-open-flags"
            tone={(data.open_flags ?? 0) > 0 ? 'rose' : 'emerald'} />
          <Stat label="Submissions" value={data.total_submissions ?? 0} testid="stat-submissions" />
          <Stat label="Missed (7d daily)"
            value={data.missed?.reduce((acc, m) => acc + (m.missed_dates?.length || 0), 0) || 0}
            testid="stat-missed"
            tone={(data.missed || []).length > 0 ? 'amber' : 'emerald'} />
        </div>
      )}

      {data?.recent_notes?.length > 0 && (
        <div className="rounded border border-white/10 bg-adm-surface/5 p-3" data-testid="insights-recent-notes">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Recent free-text notes</div>
          <ul className="space-y-1.5">
            {data.recent_notes.map((n, i) => (
              <li key={i} className="text-sm">
                <span className="text-white/50 mr-2">{n.date}</span>
                <span className="text-white/80">{n.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const STAT_TONE = {
  emerald: 'border-emerald-500/25 bg-emerald-500/5 text-emerald-300',
  amber:   'border-amber-500/25 bg-amber-500/5 text-amber-300',
  rose:    'border-rose-500/25 bg-rose-500/5 text-rose-300',
  slate:   'border-white/10 bg-adm-surface/5 text-white',
};
function Stat({ label, value, tone = 'slate', testid }) {
  return (
    <div data-testid={testid} className={`rounded border p-3 ${STAT_TONE[tone] || STAT_TONE.slate}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-medium tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// ════════════════════ Flags ════════════════════
function FlagsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracking/flags`, {
        params: statusFilter === 'all' ? {} : { status: statusFilter },
      });
      setList(r.data?.flags || []);
    } catch {
      toast.error('Could not load flags');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const setFlagStatus = async (f, status) => {
    try {
      await axios.patch(`${API}/tracking/flags/${f.id}`, { status });
      toast.success(`Flag ${status}`);
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div data-testid="flags-tab">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-white/50" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="flags-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">open</SelectItem>
              <SelectItem value="acknowledged">acknowledged</SelectItem>
              <SelectItem value="resolved">resolved</SelectItem>
              <SelectItem value="dismissed">dismissed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="border-white/15 text-white/70 hover:bg-white/5" data-testid="reload-flags-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid gap-2" data-testid="flags-list">
        {list.map((f) => (
          <div key={f.id} className={`rounded border p-3 flex items-start justify-between gap-3 flex-wrap ${SEVERITY_TONE[f.severity] || 'border-white/10 bg-adm-surface/5'}`}
            data-testid={`flag-row-${f.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-xs ${SEVERITY_TONE[f.severity] || ''}`}>{f.severity}</Badge>
                <Badge className="text-[11px] uppercase bg-slate-700/40 text-slate-300 border-slate-600">{f.status}</Badge>
                {f.assigned_to_role && <Badge className="text-[11px] bg-white/5 border-white/15 text-white/70">→ {f.assigned_to_role}</Badge>}
              </div>
              <div className="text-sm text-white/90 mt-1">{f.reason}</div>
              {f.suggested_action && <div className="text-xs text-white/60 italic">{f.suggested_action}</div>}
              <div className="text-[11px] text-white/40 mt-1">
                Subscriber: {f.subscriber_id} · Rule: {f.rule_id} · {new Date(f.created_at).toLocaleString()}
              </div>
            </div>
            {f.status === 'open' && (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setFlagStatus(f, 'acknowledged')}
                  className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                  data-testid={`ack-flag-${f.id}`}>Acknowledge</Button>
                <Button size="sm" variant="outline" onClick={() => setFlagStatus(f, 'resolved')}
                  className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid={`resolve-flag-${f.id}`}>Resolve</Button>
                <Button size="sm" variant="outline" onClick={() => setFlagStatus(f, 'dismissed')}
                  className="border-white/15 text-white/60 hover:bg-white/5"
                  data-testid={`dismiss-flag-${f.id}`}>Dismiss</Button>
              </div>
            )}
          </div>
        ))}
        {!loading && list.length === 0 && (
          <div className="text-sm text-white/40 italic">No flags match this filter.</div>
        )}
      </div>
    </div>
  );
}
